import * as React from 'react'

/**
 * Zona de arrastre para archivos Excel. Extraída de `_protected/upload.tsx`, que la usaba
 * en línea, para compartirla con las pantallas de carga de administración.
 */
export function FileDrop({
  file,
  onSelect,
  onError,
  disabled,
  hint = 'Arrastra un archivo aquí o haz clic para seleccionar (.xlsx / .xls)',
}: {
  file: File | null
  onSelect: (f: File | null) => void
  onError?: (msg: string) => void
  disabled?: boolean
  hint?: string
}) {
  const [dragging, setDragging] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)

  const esExcel = (f: File) => f.name.endsWith('.xlsx') || f.name.endsWith('.xls')

  function handleDrop(e: React.DragEvent<HTMLLabelElement>) {
    e.preventDefault()
    setDragging(false)
    if (disabled) return
    const f = e.dataTransfer.files?.[0] ?? null
    if (f && esExcel(f)) onSelect(f)
    else if (f) onError?.('Solo se aceptan archivos .xlsx o .xls')
  }

  React.useEffect(() => {
    if (!file && inputRef.current) inputRef.current.value = ''
  }, [file])

  return (
    <label
      className={`block border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      } ${dragging ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true) }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        disabled={disabled}
        onChange={(e) => onSelect(e.target.files?.[0] ?? null)}
      />
      {file ? (
        <div>
          <p className="font-medium break-all">{file.name}</p>
          <p className="text-sm text-muted-foreground mt-1">
            {(file.size / 1024 / 1024).toFixed(2)} MB
          </p>
        </div>
      ) : (
        <p className="text-muted-foreground">{hint}</p>
      )}
    </label>
  )
}
